import type { FunctionalComponent } from 'preact';
import { useEffect, useState } from 'preact/hooks';
import QRCode from 'qrcode';
import { WIDGET_MODAL_LOCK_EVENT } from '../utils/modalScrollLock';

interface Props {
  shareUrl: string;
  eventName?: string;
}

/**
 * Top-right corner QR: compact on the hero, tap to expand with animated overlay.
 */
export const EventHeroQr: FunctionalComponent<Props> = ({ shareUrl, eventName }) => {
  const [dataUrl, setDataUrl] = useState<string | null>(null);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    if (!shareUrl?.trim()) return;
    let cancelled = false;
    QRCode.toDataURL(shareUrl, {
      width: 280,
      margin: 1,
      errorCorrectionLevel: 'M',
      color: { dark: '#111111', light: '#ffffff' },
    })
      .then((u) => {
        if (!cancelled) setDataUrl(u);
      })
      .catch(() => {
        if (!cancelled) setDataUrl(null);
      });
    return () => {
      cancelled = true;
    };
  }, [shareUrl]);

  useEffect(() => {
    if (!expanded) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setExpanded(false);
    };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener('keydown', onKey);
    };
  }, [expanded]);

  useEffect(() => {
    const onLock = (e: Event) => {
      if ((e as CustomEvent<{ open: boolean }>).detail?.open) {
        setExpanded(false);
      }
    };
    window.addEventListener(WIDGET_MODAL_LOCK_EVENT, onLock);
    return () => window.removeEventListener(WIDGET_MODAL_LOCK_EVENT, onLock);
  }, []);

  if (!shareUrl?.trim()) return null;

  const label = eventName
    ? `QR code to open ${eventName} in the app`
    : 'QR code to open this event in the app';

  return (
    <>
      <div class="mw-hero-qr">
        <button
          type="button"
          class="mw-hero-qr__trigger"
          onClick={() => setExpanded(true)}
          aria-label={label}
          aria-haspopup="dialog"
          aria-expanded={expanded}
        >
          {dataUrl ? (
            <img
              src={dataUrl}
              alt=""
              class="mw-hero-qr__thumb"
              width="56"
              height="56"
            />
          ) : (
            <span class="mw-hero-qr__skeleton" aria-hidden="true" />
          )}
        </button>
      </div>
      {expanded && (
        <div class="mw-hero-qr-pop" data-open="">
          <button
            type="button"
            class="mw-hero-qr-pop__backdrop"
            onClick={() => setExpanded(false)}
            tabIndex={-1}
            aria-label="Close"
          />
          <div
            class="mw-hero-qr-pop__panel"
            role="dialog"
            aria-modal="true"
            aria-label={label}
          >
            <button
              type="button"
              class="mw-hero-qr-pop__close"
              onClick={() => setExpanded(false)}
              aria-label="Close"
            >
              ✕
            </button>
            {dataUrl && (
              <img
                src={dataUrl}
                alt={label}
                class="mw-hero-qr-pop__img"
                width="280"
                height="280"
              />
            )}
            <p class="mw-hero-qr-pop__hint">Scan to open this event in the Meeteor app</p>
          </div>
        </div>
      )}
    </>
  );
};
